/**
 * Хуки данных экрана «Номера» менеджера (Tanstack Query поверх @hsc/api-client).
 * Server-state только через React Query (патент проекта).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createRoom,
  createRoomType,
  getPublicRoomTypes,
  getRoomById,
  getRoomStatusHistory,
  listFloors,
  listRoomBoard,
  listRoomTypesAdmin,
  listRooms,
  updateRoom,
  updateRoomStatus,
  updateRoomType,
  type ListRoomsParams,
} from '@hsc/api-client';
import type {
  CreateRoomInput,
  CreateRoomTypeInput,
  FloorsResponse,
  PublicRoomTypesResponse,
  RoomBoardResponse,
  RoomListResponse,
  RoomStatusHistoryResponse,
  RoomTypeView,
  RoomTypesListResponse,
  RoomView,
  UpdateRoomInput,
  UpdateRoomStatusInput,
  UpdateRoomTypeInput,
} from '@hsc/types';

export function useRooms(params: ListRoomsParams) {
  return useQuery<RoomListResponse>({
    queryKey: ['rooms', params],
    queryFn: () => listRooms(params),
  });
}

export function useFloors() {
  return useQuery<FloorsResponse>({
    queryKey: ['rooms', 'floors'],
    queryFn: listFloors,
  });
}

/** GET /v1/rooms/board — список §4.2 (статус+чистота+уборка+гость). */
export function useRoomBoard() {
  return useQuery<RoomBoardResponse>({
    queryKey: ['rooms', 'board'],
    queryFn: listRoomBoard,
  });
}

/** GET /v1/rooms/{id}/status-history — история смены статуса с инициатором. */
export function useRoomStatusHistory(id: string | undefined) {
  return useQuery<RoomStatusHistoryResponse>({
    queryKey: ['rooms', 'status-history', id],
    queryFn: () => getRoomStatusHistory(id as string),
    enabled: Boolean(id),
  });
}

/**
 * Каталог типов номеров берём из публичного эндпоинта по slug — он отдаёт
 * id→name, а внутренний GET /v1/rooms/types требует tenant_id (UUID),
 * которого нет на фронте (platform/me даёт только slug).
 */
export function useRoomTypes(slug: string | null) {
  return useQuery<PublicRoomTypesResponse>({
    queryKey: ['room-types', slug],
    queryFn: () => getPublicRoomTypes(slug as string),
    enabled: Boolean(slug),
    staleTime: 5 * 60_000,
  });
}

/** GET /v1/rooms/{id} — карточка номера с фото (для формы редактирования). */
export function useRoom(id: string | undefined) {
  return useQuery<RoomView>({
    queryKey: ['rooms', 'detail', id],
    queryFn: () => getRoomById(id as string),
    enabled: Boolean(id),
  });
}

/** PATCH /v1/rooms/{id} — правка номера (номер/этаж/тип/фото). */
export function useUpdateRoom() {
  const qc = useQueryClient();
  return useMutation<RoomView, Error, { id: string; input: UpdateRoomInput }>({
    mutationFn: ({ id, input }) => updateRoom(id, input),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['rooms'] });
      void qc.invalidateQueries({ queryKey: ['rooms', 'detail', vars.id] });
    },
  });
}

export function useUpdateRoomStatus() {
  const qc = useQueryClient();
  return useMutation<RoomView, Error, { id: string; input: UpdateRoomStatusInput }>({
    mutationFn: ({ id, input }) => updateRoomStatus(id, input),
    onSuccess: (_data, vars) => {
      // Инвалидируем список/доску + историю статуса этого номера, чтобы
      // после смены статуса список §4.2 и таймлайн обновились.
      void qc.invalidateQueries({ queryKey: ['rooms'] });
      void qc.invalidateQueries({ queryKey: ['rooms', 'status-history', vars.id] });
    },
  });
}

/**
 * Внутренний реестр типов номеров (полные поля для управления фондом).
 * Требует tenant_id (UUID) — берётся из /v1/me. Без него запрос отключён.
 */
export function useRoomTypesAdmin(tenantID: string | undefined) {
  return useQuery<RoomTypesListResponse>({
    queryKey: ['room-types', 'admin', tenantID],
    queryFn: () => listRoomTypesAdmin(tenantID as string),
    enabled: Boolean(tenantID),
    staleTime: 60_000,
  });
}

/** Создать номер. После успеха инвалидирует списки/этажи/шахматку. */
export function useCreateRoom() {
  const qc = useQueryClient();
  return useMutation<RoomView, Error, CreateRoomInput>({
    mutationFn: createRoom,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rooms'] });
    },
  });
}

/** Создать тип номера. После успеха инвалидирует каталоги типов. */
export function useCreateRoomType() {
  const qc = useQueryClient();
  return useMutation<RoomTypeView, Error, CreateRoomTypeInput>({
    mutationFn: createRoomType,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['room-types'] });
    },
  });
}

/** Правка типа номера. После успеха инвалидирует каталоги типов. */
export function useUpdateRoomType() {
  const qc = useQueryClient();
  return useMutation<RoomTypeView, Error, { id: string; input: UpdateRoomTypeInput }>({
    mutationFn: ({ id, input }) => updateRoomType(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['room-types'] });
    },
  });
}
